import { supportedPeriods, type ElectricalTariff } from "../../../shared/commercial/electrical";

export const COMMERCIAL_SERVICE_LABELS: Record<string, string> = {
  photovoltaic: "Fotovoltaica",
  ev_charger: "Cargador de vehículo eléctrico",
  maintenance: "Mantenimiento",
};

export const COMMERCIAL_OBJECTIVE_LABELS: Record<string, string> = {
  new: "Nueva instalación",
  expand: "Ampliación",
  batteries: "Añadir baterías",
};

export const CONSUMPTION_PATTERN_LABELS: Record<string, Record<string, string>> = {
  use: { primary_residence: "Vivienda principal", second_home: "Segunda residencia", business: "Actividad empresarial", other: "Otro", unknown: "No indicado" },
  days: { weekdays: "Laborables", weekends: "Fines de semana", all_days: "Todos los días", custom: "Variable", unknown: "No indicado" },
  timeBand: { solar: "Diurna/solar", evening_night: "Tarde-noche", distributed: "Distribuida", unknown: "No indicado" },
  activity: { continuous: "Continua", shifts: "Turnos", variable_peaks: "Variable/picos", other: "Otra", unknown: "No indicado" },
};
export const FUTURE_NEED_LABELS = {
  electricVehicle: "Vehículo eléctrico",
  climatization: "Climatización",
  expansion: "Ampliación de instalación",
} as const;
export type FutureNeedStatus = "not_indicated" | "not_applicable" | "planned";
export function formatConsumptionPattern(value: unknown): string {
  if (typeof value === "string") return value.trim() ? `Texto conservado: ${value.trim()}` : "No indicado";
  if (!value || typeof value !== "object" || Array.isArray(value)) return "No indicado";
  const data = value as Record<string, unknown>;
  return Object.entries(CONSUMPTION_PATTERN_LABELS).map(([key, values]) => `${key === "timeBand" ? "Franja horaria" : key === "use" ? "Uso" : key === "days" ? "Días" : "Actividad"}: ${values[String(data[key])] ?? "No indicado"}`).join(" · ");
}
export function formatFutureNeedStatus(value: unknown): string {
  return value === "planned" ? "Previsto" : value === "not_applicable" ? "No aplica" : "No indicado";
}

export const COMMERCIAL_PHOTO_CATEGORY_LABELS: Record<string, string> = {
  roof_overview: "Vista general del tejado",
  obstacles: "Obstáculos y sombras",
  electrical_panel: "Cuadro eléctrico",
  inverter: "Ubicación del inversor",
  cable_route: "Recorrido del cableado",
  existing_equipment: "Equipos, etiquetas y accesos",
  project_document: "Proyecto / memoria",
  technical_memory: "Documentación técnica",
  equipment_label: "Etiqueta de equipo",
  other: "Otra fotografía",
};

function normalizeIdentity(value: unknown): string {
  return String(value ?? "")
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLocaleLowerCase("es")
    .replace(/[^a-z0-9]/g, "");
}

export function hasNewPvHolderContractorDifference(input: {
  serviceType?: string | null;
  kind?: string | null;
  objective?: string | null;
  supplyHolderName?: string | null;
  contractingPartyName?: string | null;
}): boolean {
  if (
    input.serviceType !== "photovoltaic" ||
    input.kind !== "particular" ||
    input.objective !== "new"
  )
    return false;
  const holder = normalizeIdentity(input.supplyHolderName);
  const contractor = normalizeIdentity(input.contractingPartyName);
  return Boolean(holder && contractor && holder !== contractor);
}

export function isGeoConfirmationCurrent(input: {
  confirmedAt?: unknown;
  confirmedAddress?: unknown;
  currentAddress?: unknown;
  latitude?: unknown;
  longitude?: unknown;
  currentLatitude?: unknown;
  currentLongitude?: unknown;
}): boolean {
  return Boolean(
    input.confirmedAt &&
      String(input.confirmedAddress ?? "").trim() === String(input.currentAddress ?? "").trim() &&
      input.latitude != null &&
      input.longitude != null &&
      input.currentLatitude != null &&
      input.currentLongitude != null &&
      Number(input.currentLatitude) === Number(input.latitude) &&
      Number(input.currentLongitude) === Number(input.longitude)
  );
}

export function commercialPendingItems(input: {
  location: Record<string, any>;
  geoConfirmed: boolean;
  photoStatus?: string;
  hasCategorizedPhotos: boolean;
  serviceType?: string | null;
  supplies: Array<Record<string, any>>;
  holderContractorDifference: boolean;
}): string[] {
  const pending: string[] = [];
  if (!input.geoConfirmed)
    pending.push(
      input.location.pending
        ? "Ubicación pendiente de confirmar"
        : "Ubicación sin confirmar en el mapa"
    );
  if (input.photoStatus !== "not_applicable" && !input.hasCategorizedPhotos)
    pending.push("Fotografías guiadas pendientes para oficina");
  if (input.serviceType === "photovoltaic") {
    const missing: string[] = [];
    if (!input.supplies.length) {
      missing.push("añadir al menos un suministro");
    } else {
      input.supplies.forEach((supply, index) => {
        const label = `Suministro ${index + 1}`;
        const tariff = supply.tariff as ElectricalTariff | undefined;
        if (!tariff || tariff === "unknown") {
          missing.push(`${label}: tarifa`);
          return;
        }
        const supplyMissing: string[] = [];
        for (const period of supportedPeriods(tariff, "contractedPower")) {
          if (!(supply.contractedPower ?? []).some((reading: any) => reading.period === period && Number.isFinite(Number(reading.value))))
            supplyMissing.push(`potencia ${period}`);
        }
        for (const period of supportedPeriods(tariff, "consumptionPeriods")) {
          if (!(supply.consumptionPeriods ?? []).some((reading: any) => reading.period === period && Number.isFinite(Number(reading.value))))
            supplyMissing.push(`consumo ${period}`);
        }
        if (supplyMissing.length) missing.push(`${label}: ${supplyMissing.join(", ")}`);
      });
    }
    if (missing.length) pending.push(`Datos eléctricos pendientes: ${missing.join(", ")}`);
  }
  if (input.holderContractorDifference)
    pending.push("Diferencia entre titular y contratante: revisión de oficina");
  return pending;
}


const BILL_FIELD_DRAFT_TARGETS: Record<string, { supply: string; party?: string }> = {
  holderName: { supply: "holderName", party: "supplyHolderName" },
  holderTaxId: { supply: "holderTaxId", party: "supplyHolderTaxId" },
  supplyAddress: { supply: "address", party: "supplyAddress" },
  cups: { supply: "cups", party: "supplyCups" },
  retailer: { supply: "retailer", party: "supplyRetailer" },
  distributor: { supply: "distributor", party: "supplyDistributor" },
  tariff: { supply: "tariff", party: "supplyTariff" },
  contractedPowerPeriods: { supply: "contractedPower", party: "power" },
  consumptionPeriods: { supply: "consumptionPeriods", party: "consumption" },
};

function sameDraftValue(left: unknown, right: unknown): boolean {
  return JSON.stringify(left) === JSON.stringify(right);
}

/** Merge only the explicitly applied OCR field from the server readback. */
export function mergeAppliedBillReadback(input: {
  currentParties: Record<string, any>;
  baselineParties: Record<string, any>;
  serverParties: Record<string, any>;
  field: string;
  supplyId?: string;
}): Record<string, any> {
  const target = BILL_FIELD_DRAFT_TARGETS[input.field];
  if (!target || !input.supplyId) return input.currentParties;
  const currentSupplies = Array.isArray(input.currentParties.supplies) ? input.currentParties.supplies : [];
  const baselineSupplies = Array.isArray(input.baselineParties.supplies) ? input.baselineParties.supplies : [];
  const serverSupplies = Array.isArray(input.serverParties.supplies) ? input.serverParties.supplies : [];
  const baselineIndex = baselineSupplies.findIndex((s: any) => s?.id === input.supplyId);
  const currentIndex = currentSupplies.findIndex((s: any) => s?.id === input.supplyId);
  const serverIndex = serverSupplies.findIndex((s: any) => s?.id === input.supplyId);
  if (baselineIndex < 0 || serverIndex < 0) return input.currentParties;

  const effectiveCurrentSupplies = currentIndex >= 0 ? [...currentSupplies] : [...baselineSupplies];
  const effectiveCurrentIndex = currentIndex >= 0 ? currentIndex : baselineIndex;
  const baselineSupply = baselineSupplies[baselineIndex];
  const currentSupply = { ...effectiveCurrentSupplies[effectiveCurrentIndex] };
  const currentValue = currentIndex >= 0 ? currentSupply[target.supply] : undefined;
  const baselineValue = baselineSupply[target.supply];
  const aliasUnchanged = !target.party || effectiveCurrentIndex !== 0 ||
    sameDraftValue(input.currentParties[target.party], input.baselineParties[target.party]);
  const supplyValueUnchanged = sameDraftValue(currentValue, baselineValue);
  const fieldUnchanged = aliasUnchanged && supplyValueUnchanged;
  const serverSupply = serverSupplies[serverIndex];
  if (fieldUnchanged && Object.prototype.hasOwnProperty.call(serverSupply, target.supply))
    currentSupply[target.supply] = serverSupply[target.supply];
  effectiveCurrentSupplies[effectiveCurrentIndex] = currentSupply;

  const parties: Record<string, any> = { ...input.currentParties, supplies: effectiveCurrentSupplies };
  if (
    fieldUnchanged && target.party && effectiveCurrentIndex === 0 &&
    Object.prototype.hasOwnProperty.call(input.serverParties, target.party)
  ) parties[target.party] = input.serverParties[target.party];
  return parties;
}


const BILL_FIELD_LOCATION_TARGETS: Record<string, string> = {
  supplyAddress: "address",
  postalAddress: "billAddress",
};

/** Merge only the explicitly applied OCR location path when it has not changed locally. */
export function mergeAppliedBillLocationReadback(input: {
  currentLocation: Record<string, any>;
  baselineLocation: Record<string, any>;
  serverLocation: Record<string, any>;
  field: string;
}): Record<string, any> {
  const key = BILL_FIELD_LOCATION_TARGETS[input.field];
  if (
    !key ||
    !Object.prototype.hasOwnProperty.call(input.serverLocation, key) ||
    !sameDraftValue(input.currentLocation[key], input.baselineLocation[key])
  ) return input.currentLocation;
  return { ...input.currentLocation, [key]: input.serverLocation[key] };
}


/** Derive a calendar-year total only when twelve distinct month records exist. */
export function deriveCompleteMonthlyTotals(facts: Array<{ period?: unknown; value?: unknown; unit?: unknown; approximate?: boolean }> = []): Array<{ year: number; value: number; unit: string; approximate?: true }> {
  const byYear = new Map<number, Map<number, number>>();
  const ambiguous = new Set<string>();
  const approximateYears = new Set<number>();
  for (const fact of facts) {
    const match = String(fact.period ?? "").match(/^(20\d{2})-(0[1-9]|1[0-2])$/);
    // Text/OCR and vector extractors emit numeric monthly facts with an explicit
    // kWh unit. Missing values are unknown months, never coercible zeroes.
    const value = fact.value;
    if (!match || typeof value !== "number" || !Number.isFinite(value) || value < 0 || fact.unit !== "kWh") continue;
    const year = Number(match[1]); const month = Number(match[2]);
    if (fact.approximate) approximateYears.add(year);
    const months = byYear.get(year) ?? new Map<number, number>();
    const key = `${year}-${month}`;
    if (months.has(month) && months.get(month) !== value) ambiguous.add(key);
    else if (!months.has(month)) months.set(month, value);
    byYear.set(year, months);
  }
  return Array.from(byYear.entries()).filter(([year, months]) => months.size === 12 && !Array.from(ambiguous).some(key => key.startsWith(`${year}-`))).map(([year, months]) => ({ year, value: Array.from(months.values()).reduce((sum, value) => sum + value, 0), unit: "kWh", ...(approximateYears.has(year) ? { approximate: true as const } : {}) }));
}
